import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { HealthcheckCoreService } from '@qtek/libs/healthcheck-core';
import {
  HTTP_BASE_URL,
  Message,
  SKIP_ERROR_HANDLING_HEADER,
} from '@qtek/shared/models';
import { checkRequestMetadata } from '@qtek/shared/utils';
import { QtHttpInterceptor } from '../qtek-interceptor';

@Injectable()
export class ErrorHandlerInterceptor implements QtHttpInterceptor {
  private baseUrl = inject(HTTP_BASE_URL);
  private matDialog = inject(MatDialog);
  private router = inject(Router);
  private healthcheck = inject(HealthcheckCoreService);
  private dialogService = inject(LazyDialogLoader);

  readonly qtek = true;

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Not a backend call, don't try to revalidate.
    // TODO(marcincichocki) use metadata instead of url when available
    if (
      !req.url.startsWith(this.baseUrl) ||
      req.url.includes('/api/v2/auth/logout') ||
      req.url.includes('/api/v2/intercom/token') ||
      req.url.includes('/api/v2/survey-embed/token')
    ) {
      return next.handle(req);
    }

    const { newReq, hasMetadata: skipErrorHandling } = checkRequestMetadata(
      req,
      SKIP_ERROR_HANDLING_HEADER
    );

    // RRRR: Remove req
    return next
      .handle(newReq)
      .pipe(
        catchError((response: HttpErrorResponse) =>
          this.handleError(response, newReq, skipErrorHandling)
        )
      );
  }

  handleError(
    response: HttpErrorResponse,
    restReq?: HttpRequest<any>,
    skipErrorHandling = false
  ) {
    console.log('HTTP ERROR: ', response.status, response);
    console.log('HTTP ERROR: ', response, restReq);

    /**
     * Set via {@link SKIP_ERROR_HANDLING_HEADER} by the caller when a given
     * error is expected/handled locally (e.g. `InvestorProfileHttpService`'s
     * "no investment targets yet" 400 on read — SD-2630). Mirrors
     * {@link RevalidateInterceptor}'s `X-Skip-Revalidate-Error-Handling`.
     */
    if (skipErrorHandling) {
      return throwError(response);
    }

    switch (response.status) {
      /**
       * We don't want to use {@link ErrorHandlerInterceptor#handleOtherStatusCode}
       * for 401 status codes.
       *
       * Revalidation is handled separately in {@link Revalidateinterceptor}.
       */
      case 400: {
        this.handle400StatusCode(response);
        break;
      }

      case 401: {
        break;
      }

      case 426: {
        this.dialogService.open('selectPlan').subscribe();

        break;
      }

      case 500: {
        // Draft save failures are non-critical — don't close all dialogs or navigate to error
        if (
          response.url?.includes('/model/StepperData') ||
          this.isTemplateApplyUrl(response.url)
        ) {
          break;
        }
        this.handle500StatusCode(response);

        break;
      }

      case 504:
      case 0: {
        this.handleNoConnection(response, restReq);
        break;
      }

      default: {
        this.handleOtherStatusCode(response);

        break;
      }
    }

    return throwError(response);
  }

  /**
   * Handle 400 status code error specifically for document service.
   */
  handle400StatusCode(response: HttpErrorResponse) {
    const isEtlUpload = !!response.url?.includes('/api/v2/etl/upload');
    const problems = isEtlUpload
      ? response.error?.res?.problems
      : response.error?.res?.pStats?.problems;
    if (
      response.url &&
      (isEtlUpload || response.url.includes('/api/v2/documents')) &&
      problems?.length > 0
    ) {
      this.saveErrorToDataLayer(response);

      const details = problems.filter((problem: any) => problem.title);

      this.dialogService
        .open('alert', {
          title: 'GNR_DOC_VALIDATION_ERR_MESSAGE',
          extendedDetails: details,
        })
        .subscribe();
    } else {
      this.handleOtherStatusCode(response);
    }
  }

  /**
   * Hadle 500 status code error specifically.
   */
  handle500StatusCode(response: HttpErrorResponse) {
    this.matDialog.closeAll();
    this.saveErrorToDataLayer(response);

    this.router.navigate(['error']);
  }

  async handleNoConnection(
    response: HttpErrorResponse,
    request: HttpRequest<any>
  ) {
    this.healthcheck.setHttpUnhealthy();
    this.saveErrorToDataLayer(response);

    if (!request.urlWithParams.includes('/api/v2/meta?a=ptl')) {
      return;
    }

    this.matDialog.openDialogs
      .filter(d => d.id !== 'error-dialog')
      .forEach(d => d.close());
    this.router.navigate(['error', 'network']);
  }

  /**
   * Handle all other status codes.
   *
   * @param response Server response, can be used to gather error info.
   */
  handleOtherStatusCode(response: HttpErrorResponse) {
    if (
      response.url?.includes('/api/v2/user/email/validate') ||
      response.url?.includes('/api/v2/deals/external-invest')
    ) {
      // Let feature-level logic handle validation/submit errors for lead bank flows.
      return;
    }

    if (response.url?.includes('/api/v2/schema/')) {
      return;
    }

    if (response.url?.includes('/api/v2/model/CompanyDomain')) {
      // WebDomainsStore reports these with translated, domain-specific keys.
      return;
    }

    if (this.isTemplateApplyUrl(response.url)) {
      // FolderStore.applyTemplate reports this with its own snackbar.
      return;
    }

    this.saveErrorToDataLayer(response);

    const { sts } = response.error || {};
    const errors = Object.keys(sts && sts.attrs ? sts.attrs : {}).map(key => ({
      key,
      ...sts.attrs[key],
    }));
    const main = errors.find(error => error.main) ||
      errors[0] || { msg: 'ERR_DEFAULT_MSG' };
    const details = errors.filter(error => !error.main);
    const messageKey = main.msg || main.key || 'ERR_DEFAULT_MSG';
    const messageParams =
      main.prms ||
      (main.msg
        ? []
        : [Object.keys(sts && sts.attrs ? sts.attrs : {}).join(', ')]);

    this.dialogService
      .open('alert', {
        message: new Message(messageKey, messageParams),
        details: details.map(detail => new Message(detail.msg, detail.prms)),
      })
      .subscribe();
  }

  private isTemplateApplyUrl(url?: string): boolean {
    return !!url && /\/deals\/[^/]+\/template\/[^/]+\/structure$/.test(url);
  }

  /**
   * Send error to google tag manager
   *
   */
  saveErrorToDataLayer(response: HttpErrorResponse) {
    if ((<any>window).dataLayer) {
      (<any>window).dataLayer.push({
        event: 'error',
        event_category: 'http_error',
        event_value: response.error,
      });
    }
  }
}
